import { BaseViewModel } from "@iot-platform/core";
import type { ReportRouteGpsFilter } from "@report/domain/models/report-route-gps.ts";
import { ReportCustomTypeEnum } from "@report/domain/models/report.model";
import { ReportGetRouteGpsUseCase } from "@report/domain/usecases/report-get-route-gps.usecase.ts";
import { StationTypeEnumSchema } from "@station/data/dto/station.dto.ts";
import { Sensor } from "@station/domain/models/sensor.model";
import type { Station } from "@station/domain/models/station.model";
import { SensorGroupGetAllUseCase } from "@station/domain/usecases/sensor-group-get-all.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

const defaultToKm = 1000;

@injectable()
export class HistoricalCustomFilterViewmodel extends BaseViewModel {
    @observable
    sensors: Readonly<Sensor>[] = [];

    @observable
    selectedSensors: number[] = [];

    @observable
    currentStation: Readonly<Station> | null = null;

    @observable
    initialLoading = true;

    @observable
    from: DateTime = DateTime.now().minus({ days: 1 });

    @observable
    to: DateTime = DateTime.now();

    @observable
    roads: string[] = [];

    @observable
    selectedRoad: string | null = null;

    @observable
    fromKm?: number = 0;

    @observable
    toKm?: number = defaultToKm;

    @observable
    reportTypes = {
        [ReportCustomTypeEnum.AvgValues]: false,
        [ReportCustomTypeEnum.MaxValues]: false,
        [ReportCustomTypeEnum.MinValues]: false,
        [ReportCustomTypeEnum.SumValues]: false,
        [ReportCustomTypeEnum.Measures]: false,
        [ReportCustomTypeEnum.TotalSum]: false,
        [ReportCustomTypeEnum.Tendency]: false,
    };

    @observable
    errors: { from?: string; to?: string } = {};

    constructor(
        @inject(SensorGroupGetAllUseCase)
        private readonly sensorGroupGetAllUseCase: SensorGroupGetAllUseCase,
        @inject(ReportGetRouteGpsUseCase)
        private readonly reportGetRouteGpsUseCase: ReportGetRouteGpsUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.fetchSensorGroups(), this.fetchRouteGps()]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    setReportType = (
        reportType: ReportCustomTypeEnum,
        isChecked: boolean,
    ): void => {
        runInAction(() => {
            this.reportTypes[reportType] = isChecked;
        });
    };

    setStation = (station: Readonly<Station>): void => {
        runInAction(() => {
            this.currentStation = station;
        });
    };

    @action
    private async fetchSensorGroups(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        const sensorGroups = await this.sensorGroupGetAllUseCase.execute(
            this.currentStation.id.value,
        );

        // Dedupe sensors that share the same `code` across vargroups (e.g.
        // TA_0/HR_0 declared in two vargroups); they render with the same
        // translated label and would otherwise duplicate in the selector.
        // Sensors with `code === null` are kept as-is — their fallback
        // label is `sensor.name`, which is per-row distinct.
        const seenCodes = new Set<string>();
        const sensors: Readonly<Sensor>[] = [];
        for (const sensorGroup of sensorGroups.sensorGroups) {
            for (const sensor of sensorGroup.sensors) {
                if (sensor.code !== null) {
                    if (seenCodes.has(sensor.code)) continue;
                    seenCodes.add(sensor.code);
                }
                sensors.push(sensor);
            }
        }

        runInAction(() => {
            this.sensors = sensors;
        });
    }

    @action
    async fetchRouteGps(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        if (this.currentStation.type === StationTypeEnumSchema.enum.Gps) {
            const reportRouteGpsFilter: ReportRouteGpsFilter = {
                from: this.from.toSeconds(),
                to: this.to.toSeconds(),
                stationId: Number(this.currentStation.id.value),
            };
            const routeGps =
                await this.reportGetRouteGpsUseCase.execute(
                    reportRouteGpsFilter,
                );

            this.roads = routeGps.map((route) => route.roads).flat();
        }
    }

    @action
    setSelectedRoad = (road: string | null): void => {
        if (road === null) {
            this.setFromKm(0);
            this.setToKm(defaultToKm);
        }
        runInAction(() => {
            this.selectedRoad = road;
        });
    };

    @action
    setFromKm = (fromKm: number): void => {
        runInAction(() => {
            this.fromKm = fromKm;
        });
    };

    @action
    setToKm = (toKm: number): void => {
        runInAction(() => {
            this.toKm = toKm;
        });
    };
}
